import * as React from 'react'
import { LoadScript } from '@react-google-maps/api'
import { GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/compat/router'
import getConfig from 'next/config'
import Head from 'next/head'

import {
  BreadcrumbsRenderer,
  StoreDetailsCard,
} from '@thg-commerce/enterprise-components'
import { StoreFacility } from '@thg-commerce/enterprise-components/StoreDetailsCard/Facilities/Facilities'
import { openGoogleDirectionMap } from '@thg-commerce/enterprise-components/StoreMap/utils'
import {
  EnterpriseNextPage,
  i18n,
  PageWrapper,
  Routes,
  useSiteConfig,
  useSiteDefinition,
  withCacheConfiguration,
} from '@thg-commerce/enterprise-core'
import { PageType } from '@thg-commerce/enterprise-core/src/App/types'
import { loadConfiguration } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/ConfigurationLoader'
import { NextConfig } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/types'
import { createApolloWithRequest } from '@thg-commerce/enterprise-core/src/Factory/apollo'
import { FullHeaderLayout } from '@thg-commerce/enterprise-core/src/Layouts'
import {
  EnterpriseRequest,
  EnterpriseResponse,
} from '@thg-commerce/enterprise-core/src/Server/types'
import { Status } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/StoreByStoreUrlTag'
import {
  processesLowerCase,
  Stores as StoresResolver,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/Stores'
import { clientConfig as clientConfigFactory } from '@thg-commerce/enterprise-network/src/ApolloProvider/utils'
import { DayOfWeek } from '@thg-commerce/enterprise-network/src/generated/graphql'
import { spacing } from '@thg-commerce/enterprise-theme'
import { BreadcrumbItem } from '@thg-commerce/gravity-elements'
import { GridItem } from '@thg-commerce/gravity-system'
import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'

import { Stores as STORES_QUERY } from '../../../../../../src/graphql/Stores.graphql'
import { StoreSearchRenderer } from '../../../../../../src/renderer/StoreSearchRenderer'
import { isStoreLocatorOpen, replaceString } from '../../../../../../src/utils'
import CustomError from '../../../../../_error'
import { processedTitle } from '../index'

interface CityProps {
  cityData: {
    id: string
    displayName: string
    urlTag: string
    phoneNumber: string
    email?: string
    longitude: number
    latitude: number
    openingTimes: {
      day: DayOfWeek
      openingTime: string
      closingTime: string
    }[]
    address: {
      country: string
      addresseeName: string
      addressLine1: string
      addressLine2: string
      addressLine3: string
      addressLine4: string
      addressLine5: string
      postalCode: string
    }
    storeFacility: StoreFacility[]
  }[]
  statusCode: number
}

const Town: EnterpriseNextPage<CityProps> = ({ cityData, statusCode }) => {
  const {
    enableStoreLocator,
    geocodeApiKeyStoreMap,
    storeLocatorCountry,
    enableAlternateStoreUrl,
  } = useSiteConfig()
  const { originUrl } = useSiteDefinition()
  const router = useRouter()
  const {
    query: { town, country, region },
  } = router
  const [scriptReady, setScriptReady] = React.useState<boolean>(false)

  const townId = town && typeof town === 'string' ? town : ''
  const countryString = country && typeof country === 'string' ? country : ''
  const regionString = region && typeof region === 'string' ? region : ''

  const i18nText = {
    townTitle: i18n('stores.city.page.title'),
    getDirectionsButton: i18n('storelocator.storedetails.directionsbutton'),
    storeDetailsButton: i18n('storelocator.storedetails.detailsbutton'),
    storePermClosed: i18n('storelocator.storedetails.permanentlyclosed'),
    storeTempClosed: i18n('storelocator.storedetails.temporarilyclosed'),
    storeClosedText: i18n('storelocator.storedetails.closed'),
    storeOpenText: i18n('storelocator.storedetails.open'),
    storesRegions: i18n('stores.regions.head.title'),
    countryTitle: i18n('stores.country.page.title'),
    breadCrumbsHome: i18n('general.breadcrumbs.home.text'),
    storesRegionsBreadcrumb: i18n('breadcrumbs.all.stores'),
    storeSearchSubtitle: i18n('storelocator.storesearch.subtitle'),
    storeSearchInputLabel: i18n('storelocator.storesearch.inputlabel'),
    storeSearchButtonLabel: i18n('storelocator.storesearch.buttonlabel'),
    storeSearch: {
      title: i18n(
        'storelocator.storsearch.title',
        processedTitle(replaceString(townId)),
      ),
      subtitle: i18n('storelocator.storesearch.subtitle'),
      inputLabel: i18n('storelocator.storesearch.inputlabel'),
      buttonLabel: i18n('storelocator.storesearch.buttonlabel'),
      infoLabel: i18n('storelocator.storesearch.infolabel'),
      infoLinkLabel: i18n('storelocator.storesearch.infolinklabel'),
    },
  }

  if (
    !enableStoreLocator ||
    country !== storeLocatorCountry ||
    statusCode === 404
  ) {
    return <CustomError statusCode={404} />
  }

  const sessionStorage = typeof window !== 'undefined' && window.sessionStorage

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      text: i18nText.breadCrumbsHome,
      url: Routes.HomePage,
    },
    {
      text: i18nText.storesRegionsBreadcrumb,
      url: Routes.Stores.replace(':country', countryString.toString()),
    },
    {
      text: replaceString(regionString),
      url: Routes.StoresRegion.replace(':country', countryString).replace(
        ':region',
        regionString,
      ),
    },
    {
      text: replaceString(townId),
      url: Routes.StoresCity.replace(':country', countryString)
        .replace(':region', regionString)
        .replace(':town', townId),
    },
  ]

  const storeDetailsHref = (
    country: string,
    region: string,
    town: string,
    storeUrlTag: string,
  ) => {
    const routeTemplate = enableAlternateStoreUrl
      ? Routes.StoreDetailsNew
      : Routes.StoreDetails

    return routeTemplate
      .replace(':country', processesLowerCase(country || ''))
      .replace(':region', processesLowerCase(region || ''))
      .replace(':town', processesLowerCase(town || ''))
      .replace(':store', storeUrlTag)
  }

  return (
    <React.Fragment>
      <Head>
        <title>{processedTitle(replaceString(townId))}</title>
        <link
          rel="canonical"
          href={`${originUrl}${Routes.StoresCity.replace(
            ':country',
            countryString,
          )
            .replace(':region', regionString)
            .replace(':town', townId)}`}
          key="link_canonical"
        />
      </Head>
      <BreadcrumbsRenderer
        breadcrumbLabel={i18nText.townTitle}
        breadcrumbs={breadcrumbItems}
        schemaItems={breadcrumbItems.map((breadcrumb, index) => {
          return {
            '@type': 'ListItem',
            position: index + 1,
            name: breadcrumb.text,
            item: originUrl.concat(breadcrumb.url),
          }
        })}
        capitalizeText
      />
      <PageWrapper
        compactMargin={true}
        enableMaxWidth={false}
        sendPageVisit={true}
      >
        <GridItem
          colSpan={12}
          rowStart={2}
          style={{
            paddingBottom: `${spacing(4)}`,
          }}
        >
          <StoreSearchRenderer
            i18nText={i18nText.storeSearch}
            alignment={HorizontalAlignment.CENTER}
            isSearchReady={scriptReady}
            onChangeRedirect={(value) =>
              router.push(`${Routes.StoreLocator}?location=${value}`)
            }
          />
        </GridItem>
        <GridItem
          colStart={1}
          colSpan={12}
          rowSpan={12}
          style={{
            padding: `0 ${spacing(2)} ${spacing(10)}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: `${spacing(2)}`,
              justifyContent: 'center',
            }}
          >
            {cityData.map((item, index) => (
              <StoreDetailsCard
                i18n={{
                  getDirectionsButton: i18nText.getDirectionsButton,
                  storeDetailsButton: i18nText.storeDetailsButton,
                  storePermClosed: i18nText.storePermClosed,
                  storeTempClosed: i18nText.storeTempClosed,
                  storeClosedText: i18nText.storeClosedText,
                  storeOpenText: i18nText.storeOpenText,
                }}
                store={{
                  ...item,
                  address: `${
                    item.address.addressLine1
                      ? `${item.address.addressLine1},`
                      : ''
                  } ${
                    item.address.addressLine2
                      ? `${item.address.addressLine2},`
                      : ''
                  } ${
                    item.address.addressLine3
                      ? `${item.address.addressLine3},`
                      : ''
                  } ${
                    item.address.addressLine4
                      ? `${item.address.addressLine4},`
                      : ''
                  } ${item.address.postalCode ? item.address.postalCode : ''} `,
                  status: isStoreLocatorOpen(item.openingTimes),
                  isOpen: isStoreLocatorOpen(item.openingTimes) === Status.OPEN,
                }}
                key={`${item.id}-${index}`}
                storeDetailsOnClick={() => {
                  if (sessionStorage) {
                    sessionStorage.setItem('storeDetailsPrevPath', 'indexPage')
                  }
                }}
                storeDetailsHref={storeDetailsHref(
                  countryString.toString(),
                  regionString.toString(),
                  townId.toString(),
                  item.urlTag,
                )}
                getDirectionsHref={openGoogleDirectionMap(
                  `${item.latitude},${item.longitude}`,
                )}
              />
            ))}
          </div>
        </GridItem>
      </PageWrapper>
      <LoadScript
        id="google-map"
        googleMapsApiKey={geocodeApiKeyStoreMap || ''}
        libraries={['places']}
        loadingElement={<React.Fragment />}
        onLoad={() => setScriptReady(true)}
      />
    </React.Fragment>
  )
}

export default Town

Town.Layout = FullHeaderLayout
Town.pageType = PageType.STORE_LOCATOR

export const getServerSideProps = withCacheConfiguration<
  {
    props: CityProps
  },
  GetServerSidePropsContext
>(async (context) => {
  const nextConfig = getConfig() as NextConfig
  const request = context.req as EnterpriseRequest
  const response = context.res as EnterpriseResponse
  const config = await loadConfiguration(nextConfig, request)
  const apollo = createApolloWithRequest(request, config, true)

  const { originUrl } = config.publicRuntimeConfig.siteDefinition
  const { publicRuntimeConfig } = config

  const allowOverrides = request.headers['x-enterprise-allow-overrides'] === '1'
  const allowSitePropertyOverrides =
    request.headers['x-enterprise-allow-site-property-overrides'] === '1'

  const clientConfig = clientConfigFactory(
    publicRuntimeConfig,
    allowOverrides,
    allowSitePropertyOverrides,
    request.config.enableVary,
  )

  apollo.setResolvers({
    Query: {
      stores: StoresResolver(apollo, clientConfig, publicRuntimeConfig),
    },
  })

  const { country } = context.query
  const invalidPath =
    country !== publicRuntimeConfig.siteConfig.storeLocatorCountry

  if (context.params) {
    const { data } = await apollo.query({
      query: STORES_QUERY,
      fetchPolicy: 'no-cache',
      variables: {
        region: context.params.region,
        city: context.params.town,
      },
    })

    if (!data || !data.stores || invalidPath) {
      response.statusCode = 404
      return {
        props: {
          originUrl,
          cityData: [],
          statusCode: response.statusCode,
        },
      }
    }

    return {
      props: {
        originUrl,
        cityData: data.stores || [],
        statusCode: response.statusCode,
      },
    }
  }
  response.statusCode = 404
  return {
    props: {
      originUrl,
      cityData: [],
      statusCode: response.statusCode,
    },
  }
})
